import React, { useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { InclusionAssetEditValue } from './InclusionAssetEditDialog';
import InclusionAssetEditFields from './InclusionAssetEditFields';
import { type PackageOption, clearPackageOptions, filterPackageOptions, selectPackageOptions } from './utils';

interface Props {
  isSaving?: boolean;
  open: boolean;
  packageOptions: Array<PackageOption>;
  onAdd: (inclusion: InclusionAssetEditValue, packageIds: Array<string>) => Promise<void>;
  onClose: () => void;
}

const DEFAULT_INCLUSION: InclusionAssetEditValue = {
  category: '',
  categoryId: null,
  currency: '',
  isHidden: false,
  name: '',
  requiresBooking: false,
  rrp: 0,
  type: 'Flash',
  unit: '',
};

export default function AddInclusionDialog({ isSaving, open, packageOptions, onAdd, onClose }: Props) {
  const [value, setValue] = useState<InclusionAssetEditValue>(DEFAULT_INCLUSION);
  const [packageIds, setPackageIds] = useState<Array<string>>([]);
  const [packageSearch, setPackageSearch] = useState('');
  const isSinglePackage = packageOptions.length === 1;
  const visiblePackageOptions = filterPackageOptions(packageOptions, packageSearch);
  const hasUnselectedVisiblePackages = visiblePackageOptions.some((option) => !packageIds.includes(option.id));
  const hasSelectedVisiblePackages = visiblePackageOptions.some((option) => packageIds.includes(option.id));
  const selectedPackageIds = isSinglePackage ? [packageOptions[0].id] : packageIds;
  const canAdd =
    !!value.name.trim() && !!value.category.trim() && !!value.unit?.trim() && selectedPackageIds.length > 0;

  const resetAndClose = () => {
    setValue(DEFAULT_INCLUSION);
    setPackageIds([]);
    setPackageSearch('');
    onClose();
  };

  const togglePackage = (packageId: string) => {
    setPackageIds((currentPackageIds) =>
      currentPackageIds.includes(packageId)
        ? currentPackageIds.filter((currentPackageId) => currentPackageId !== packageId)
        : currentPackageIds.concat(packageId),
    );
  };

  const handleAdd = async () => {
    try {
      await onAdd(value, selectedPackageIds);
      resetAndClose();
    } catch {
      return;
    }
  };

  return (
    <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth="md">
      <DialogTitle>Create custom inclusion</DialogTitle>
      <DialogContent dividers>
        <InclusionAssetEditFields open={open} value={value} onChange={setValue} />

        {!isSinglePackage && (
          <Box sx={{ mt: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Packages</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="text"
                  size="small"
                  disabled={!hasUnselectedVisiblePackages}
                  onClick={() =>
                    setPackageIds((currentPackageIds) => selectPackageOptions(currentPackageIds, visiblePackageOptions))
                  }
                >
                  Select all
                </Button>
                <Button
                  variant="text"
                  size="small"
                  disabled={!hasSelectedVisiblePackages}
                  onClick={() =>
                    setPackageIds((currentPackageIds) => clearPackageOptions(currentPackageIds, visiblePackageOptions))
                  }
                >
                  Clear
                </Button>
              </Stack>
            </Stack>
            <TextField
              fullWidth
              size="small"
              placeholder="Search packages"
              value={packageSearch}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              onChange={(event) => setPackageSearch(event.target.value)}
            />
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 280, mt: 1, overflowY: 'auto' }}>
              {visiblePackageOptions.map((option) => (
                <MenuItem key={option.id} onClick={() => togglePackage(option.id)} sx={{ gap: 1, py: 1 }}>
                  <Checkbox size="small" checked={packageIds.includes(option.id)} sx={{ p: 0.25 }} />
                  <ListItemText primary={option.name} />
                </MenuItem>
              ))}
              {visiblePackageOptions.length === 0 && (
                <MenuItem disabled>
                  <ListItemText primary={packageOptions.length === 0 ? 'No packages available' : 'No packages found'} />
                </MenuItem>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" disabled={isSaving} onClick={resetAndClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canAdd || isSaving} onClick={() => void handleAdd()}>
          {isSaving ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
